import './app.css';
import {app, events, EventType, onair, wpi} from "./modules/config.ts";
import {useEffect, useState} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {Api} from "./modules/api.ts";
import {Utils} from "./modules/utils.ts";
import {useDispatch} from "react-redux";
import type {ApiResponse} from "./modules/interfaces.ts";
import type {DaoApiInit} from "./daos/dao-api-init.ts";
import type {DaoInit} from "./daos/dao-init.ts";
import UiLoader from "./pages/component/ui.loader.tsx";
import UiError from "./pages/component/ui.error.tsx";
import PageLogin from "./pages/auth/login.tsx";
import PageMain from "./pages/main";
import Page404 from "./pages/errors/404.tsx";
import PageSplash from "./pages/splash";
import PageChannel from "./pages/main/channel";
import PageAnnounce from "./pages/main/announce";
import PageAiVoice from "./pages/main/ai-voice";
import Dialog from "./pages/component/dialog.tsx";
import Loader from "./pages/component/loader.tsx";
import Modal from "./pages/component/modal.tsx";
import {closeModal, type ModalStateData, openModal} from "./stores/modal.slice.ts";

function App() {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  const networkChangeHandler = (status: unknown, message: unknown) => {
    const code = status as number;           // 1 | 0 | -1
    const online = code === 1;
    const slow = code === -1;
    const msg = (message as string) ?? '';

    app.online = online;
    app.slow = slow;

    if (code === 0) dispatch(openModal({ type: 'offline', data: msg as unknown as ModalStateData }));
    else if (code === -1) dispatch(openModal({ type: 'slow', data: msg as unknown as ModalStateData }));  // 또는 toast
    else dispatch(closeModal());

    // slow 일 때는 online 유지할지 정책 결정 필요
    onair.offline(!online);
  };

  useEffect(() => {
    events.on(EventType.NETWORK_CHANGE, networkChangeHandler);
    return () => {
      events.off(EventType.NETWORK_CHANGE, networkChangeHandler);
    }
  }, []);

  useEffect(() => {
    (async () => {
      Api.init()
        .then(async (rs: ApiResponse<DaoApiInit>) => {
          app.ip = rs.data.ip as string ?? null;

          const dao = {
            c08: rs.data.C08,
            c16: rs.data.C16,
            cpv: rs.data.CPV,
            cpx: rs.data.CPX,
            date: rs.data.DATE
          } as DaoInit;

          if(dao.date) {
            // Onair 초기설정
            await onair.init(Utils.toDate(dao.date)?.getTime());

            dao.crypt_key = dao.date
              .split(" ")
              .join("T")
              .split("")
              .reverse()
              .join("")
              .substring(0, (dao.cpx ? 16: 8));
          }

          if(dao.cpv) {
            dao.crypt_iv = dao.cpv.slice(-16);
          }

          dao.cryption = Utils.decrypt((dao.cpx ? dao.c16: dao.c08) as string, dao.crypt_key, dao.crypt_iv) ?? '';
          await app.init(dao);

          // 앱 버전 인출
          app.app_version = await wpi.version();

          setReady(true);
        })
        .catch(() => setError(true));
    })();
  }, []);

  if (error) return <UiError msg="서버에 연결할수 없습니다." />;
  if (!ready) return <UiLoader />;

  return (
    <>
      <Loader />
      <Modal />
      <Dialog />
      <BrowserRouter future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}>
        <Routes>
          <Route path="/" element={<PageSplash />} />
          <Route path="/login" element={<PageLogin />} />
          <Route path="/main" element={<PageMain />}>
            <Route index element={<PageChannel />} />
            <Route path="channel" element={<PageChannel />} />
            <Route path="announce" element={<PageAnnounce />} />
            <Route path="ai-voice" element={<PageAiVoice />} />
          </Route>
          <Route path="*" element={<Page404 />} />
        </Routes>
      </BrowserRouter>
    </>

  )
}

export default App;
